<template>
	<div class="results-toolbar__filter-bar">
		<div class="results-toolbar__filter">
			<div class="results-toolbar__left">
				<Search
					variant="toggle-icons"
					v-model="filters.search"
					:timeout="300"
					placeholder="Search by user name or email"
					@change="emitValues"
				/>
				<DropdownSort
					:options="ddsOptions"
					:showClearSelectedButton="false"
					v-model="ddsValue"
					@change="emitValues"
				/>
			</div>
			<div class="results-toolbar__right">
				<Button
					v-if="showResetFilters"
					class="results-toolbar__restore-filters"
					className="btn--link"
					labelText="Restore Filters"
					:btnClick="resetFilters"
				/>

				<div class="results-toolbar__filter-desktop" ref="desktopRef">
					<DropdownV2
						name="userTypes"
						:showClearSelectedButton="true"
						v-model="filters.clientId"
						:options="userTypesOptions"
						dropdownWidth="165px"
						@change="emitValues"
					/>
					<DropdownV2
						name="status"
						:showClearSelectedButton="true"
						v-model="filters.status"
						:options="statusOptions"
						dropdownWidth="165px"
						@change="emitValues"
					/>
					<DropdownV2
						name="roles"
						:options="optionRoles"
						:showClearSelectedButton="true"
						v-model="filters.roleId"
						placeholder="All Roles"
						:moreToLoad="$parent.hasMoreRolesToLoad"
						@load-more="$emit('load-more-roles')"
						@search="$emit('search-roles', $event)"
						dropdownWidth="165px"
						@change="emitValues"
						ref="rolesDropdownRef"
					/>
				</div>
				<div class="results-toolbar__filter-tablet" ref="mobileRef">
					<Button
						className="btn--primary"
						:labelText="userFilterLabel"
						iconClassName="icon-filter"
						:iconBeforeLabel="true"
						:btnClick="() => (displayFiltersModal = true)"
					/>
				</div>
				<Modal
					v-if="displayFiltersModal"
					:display="true"
					title="Filters"
					@show="displayFiltersModal = false"
					ref="modalFilterRef"
				>
					<template #modal-content>
						<div class="results-toolbar__filter-modal">
							<DropdownV2
								name="userTypes"
								:showClearSelectedButton="true"
								v-model="filters.clientId"
								:options="userTypesOptions"
								dropdownWidth="300px"
							/>
							<DropdownV2
								name="status"
								:showClearSelectedButton="true"
								v-model="filters.status"
								:options="statusOptions"
								dropdownWidth="300px"
							/>
							<DropdownV2
								name="roles"
								:options="optionRoles"
								:showClearSelectedButton="true"
								v-model="filters.roleId"
								placeholder="All Roles"
								:moreToLoad="$parent.hasMoreRolesToLoad"
								@load-more="$emit('load-more-roles')"
								@search="$emit('search-roles', $event)"
								@clean-search="cleanSearchDropdown"
								dropdownWidth="300px"
								ref="rolesDropdownModalRef"
							/>
						</div>
					</template>
					<template #modal-footer>
						<ButtonGroup>
							<template #container>
								<Button
									labelText="Apply"
									className="btn--primary"
									:btnClick="applyFilterUsingModal"
									:disabled="!filtersModalHaveChanges"
								/>
								<Button
									labelText="Cancel"
									className="btn--link"
									:btnClick="closeModalFilters"
								/>
							</template>
						</ButtonGroup>
					</template>
				</Modal>
			</div>
		</div>
	</div>
</template>

<script>
import Button from '@/components/forms/button/button';
import DropdownV2 from '@/components/forms/dropdown/dropdown-v2.vue';
import Modal from '@/components/shells/modal/modals';
import ButtonGroup from '@/components/forms/button/button-group';
import Search from '@/components/forms/search/search';
import DropdownSort from '@/components/forms/dropdown/dropdown-sort';
import filtersFieldsCustomMixin from '@/mixins/filters-fields-custom-mixin';
import { deepEqualNoFn } from '@/utils/object';
import { countsDiffObjects } from '@/utils/function';
import _ from 'lodash';

export default {
	name: 'UserFilters',
	components: {
		DropdownV2,
		Button,
		Modal,
		ButtonGroup,
		Search,
		DropdownSort
	},
	model: {
		prop: 'value'
	},
	props: {
		value: {
			type: Object,
			default: () => {}
		},
		className: {
			type: String
		},
		defaultFilters: Object,
		optionRoles: Array
	},
	mixins: [filtersFieldsCustomMixin],
	data() {
		return {
			displayFiltersModal: false,
			filters: {},
			currentDefaultFilters: {
				...this.value
			},
			statusOptions: [
				{
					text: 'All Statuses',
					value: null
				},
				{
					text: 'Active',
					value: 'active'
				},
				{
					text: 'Inactive',
					value: 'inactive'
				},
				{
					text: 'Locked',
					value: 'locked'
				},
				{
					text: 'Corrupted',
					value: 'corrupted'
				}
			],
			userTypesOptions: [
				{
					text: 'All User Types',
					value: null
				},
				{
					text: 'Consultant User',
					value: 'vue-support-external'
				},
				{
					text: 'Internal User',
					value: 'vue-support-portal'
				}
			],
			ddsOptions: [
				{ text: 'Name', value: 'name' },
				{ text: 'Status', value: 'status' }
			],
			keysToPick: ['clientId', 'status', 'roleId']
		};
	},
	created() {
		this.updateLocalValues(this.value);
	},
	computed: {
		filtersModalHaveChanges() {
			return !deepEqualNoFn(this.filters, this.value, true);
		},
		totalFiltersNumber() {
			return countsDiffObjects(
				{ ...this.defaultFilters },
				{ ...this.filters },
				['search', 'sortby', 'sortdirection']
			);
		},
		userFilterLabel() {
			if (this.totalFiltersNumber) {
				return ` Filters (${this.totalFiltersNumber})`;
			}
			return ' Filters';
		},
		ddsValue: {
			get() {
				return {
					sortby: this.filters.sortby,
					sortdirection: this.filters.sortdirection
				};
			},
			set(value) {
				this.filters.sortby = value.sortby;
				this.filters.sortdirection = value.sortdirection;
			}
		},
		showResetFilters() {
			return !deepEqualNoFn(
				this.filteredFilters,
				this.filteredCurrentDefaultFilters,
				true
			);
		},
		filteredFilters() {
			return _.pick(this.filters, this.keysToPick);
		},
		filteredCurrentDefaultFilters() {
			return _.pick(this.defaultFilters, this.keysToPick);
		}
	},
	methods: {
		updateLocalValues(obj) {
			if (!deepEqualNoFn(this.filters, obj, true)) {
				this.filters = _.cloneDeep(obj);
			}
		},
		applyFilterUsingModal() {
			this.displayFiltersModal = false;
			this.$emit('input', { ...this.filters });
		},
		resetFilters() {
			const resetFilters = Object.assign(
				{},
				this.filters,
				this.filteredCurrentDefaultFilters
			);
			this.updateLocalValues(resetFilters);
			this.currentDefaultFilters = _.cloneDeep(this.filters);
			this.$emit('input', { ...this.filters });
			this.$refs.rolesDropdownRef.$refs.dropdownListRef.searchQuery = '';
		},
		cleanSearchDropdown() {
			this.$refs.modalFilterRef.$parent.$refs.rolesDropdownModalRef.$refs.dropdownListRef.searchQuery =
				'';
		},
		closeModalFilters() {
			this.filtersModal = _.cloneDeep(this.filters);
			this.displayFiltersModal = false;
		},
		emitValues() {
			if (!deepEqualNoFn(this.filters, this.value, true)) {
				this.$emit('input', { ...this.filters });
			}
		},
		getModelValues() {
			const status = this.statusOptions.find(
				element => element.value === this.value.status
			);

			const client = this.userTypesOptions.find(
				element => element.value === this.value.clientId
			);

			const role = this.optionRoles.find(
				rol => rol.value === this.value.roleId
			);

			const obj = {};
			if (status) {
				obj.filterValue1 = status.text;
			}
			if (client) {
				obj.filterValue2 = client.text;
			}
			if (role) {
				obj.filterValue3 = role.text;
			}

			return obj;
		}
	},
	watch: {
		value: {
			handler(newValue) {
				this.updateLocalValues(newValue);
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.results-toolbar {
	&__filter-bar {
		display: inline;
		width: 100%;
	}
	&__left {
		display: flex;
		gap: $gutter-lg;
	}
	&__right {
		display: flex;
	}

	&__filter {
		.dropdown {
			margin: 0;
		}
	}
	&__restore-filters {
		margin-right: $gutter-lg;
	}
	&__filter-modal {
		display: grid;
		gap: 12px;
	}
	&__filter-tablet {
		@include mq($from: lg) {
			display: none;
		}
	}
	&__filter-desktop {
		display: none;

		@include mq($from: lg) {
			display: flex;
			gap: $gutter-lg;
		}
	}
}
@media (min-width: 48em) {
	.results-toolbar.results-toolbar--variant-three .results-toolbar__filter {
		justify-content: space-between;
		display: flex;
		width: 100%;
	}
}
</style>
